// Tab 1: Stoc curent — main inventory table with inline editing (min_stock,
// supplier assignment, stock adjustment) and a quick "Intrări marfă" action.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Loader2, ImageOff, ExternalLink, Plus, X, Save, Boxes,
  AlertTriangle, TrendingDown, RefreshCw, History, FileSearch, Settings as SettingsIcon, Tag,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

import {
  listItems, updateItem, adjustStock, listSuppliers, stockIn,
  fulfillmentFullRebuild, fulfillmentSyncNow, getItemAudit, getLedgerStatus,
  getReservationTags, updateReservationTags, autocompleteOrderTags,
} from "@/lib/inventory";
import { ReservationTagsDialog } from "./ReservationTagsDialog";

const FILTERS = [
  { id: "all", label: "Toate" },
  { id: "supply", label: "Necesită aprovizionare", icon: AlertTriangle },
  { id: "oversold", label: "Supravândute", icon: TrendingDown },
  { id: "low", label: "Stoc redus" },
  { id: "out", label: "Stoc 0" },
  { id: "ok", label: "OK" },
];

const PRODUCT_STATUS_FILTERS = [
  { id: "all", label: "Toate" },
  { id: "active", label: "Active" },
  { id: "draft", label: "Draft" },
  { id: "archived", label: "Arhivate" },
];

const STATUS_STYLES = {
  ok: { label: "OK", className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
  low: { label: "Stoc redus", className: "bg-amber-500/10 text-amber-500 border-amber-500/30" },
  out: { label: "Stoc 0", className: "bg-red-500/10 text-red-500 border-red-500/30" },
  supply: { label: "Necesită aprovizionare", className: "bg-purple-500/10 text-purple-500 border-purple-500/30" },
};

export default function StockCurrentTab({ onChanged }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [productStatusFilter, setProductStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [stockInOpen, setStockInOpen] = useState(false);
  const [auditItem, setAuditItem] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [reservationTagsOpen, setReservationTagsOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, status: filter, product_status: productStatusFilter, limit: 200 };
      if (supplierFilter) params.supplier_id = supplierFilter;
      const r = await listItems(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  }, [search, filter, productStatusFilter, supplierFilter]);

  useEffect(() => {
    const t = setTimeout(reload, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [reload, search]);

  useEffect(() => {
    listSuppliers().then(setSuppliers).catch(() => {});
    getLedgerStatus().then(setLedger).catch(() => {});
  }, []);

  const onItemChanged = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  return (
    <div className="space-y-3" data-testid="stock-current-tab">
      {/* Ledger sync banner */}
      <div className="rounded-lg border border-border bg-gradient-to-r from-primary/5 to-emerald-500/5 px-4 py-3 flex items-center gap-4 flex-wrap" data-testid="ledger-banner">
        <RefreshCw className="h-4 w-4 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-[240px] text-xs leading-snug">
          <div className="font-semibold text-sm">Stoc = Σ intrări marfă − Σ comenzi fulfilled</div>
          <div className="text-muted-foreground">
            Stoc Shopify este IGNORAT. Disponibil real = stoc fizic − produse rezervate în comenzi confirmate.
            Sync incremental automat la fiecare 30 min.
            {ledger?.last_fulfillment_sync_at && (
              <> · Ultim sync: <span className="font-mono">{new Date(ledger.last_fulfillment_sync_at).toLocaleString("ro-RO")}</span></>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          disabled={syncing || rebuilding}
          data-testid="ledger-sync-now"
          onClick={async () => {
            setSyncing(true);
            try {
              const r = await fulfillmentSyncNow();
              toast.success(`Sync incremental: ${r.scanned} comenzi · +${r.fulfilled_added} sale · ${r.items_recomputed} items refăcute`);
              await reload();
              getLedgerStatus().then(setLedger).catch(() => {});
              onChanged?.();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Sync eșuat");
            } finally { setSyncing(false); }
          }}
        >
          {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Sync acum
        </Button>
        <Button
          size="sm"
          className="h-8"
          disabled={syncing || rebuilding}
          data-testid="ledger-full-rebuild"
          onClick={async () => {
            if (!window.confirm("Recalculează TOATE stocurile? Va șterge ledger-ul de vânzări și-l va reconstrui din comenzile fulfilled. Intrările de marfă nu sunt afectate.")) return;
            setRebuilding(true);
            try {
              const r = await fulfillmentFullRebuild();
              toast.success(`Recalculat · ${r.orders_scanned} comenzi · ${r.sale_movements_created} sale-uri · ${r.items_recomputed} stocuri actualizate`);
              await reload();
              getLedgerStatus().then(setLedger).catch(() => {});
              onChanged?.();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Recalcul eșuat");
            } finally { setRebuilding(false); }
          }}
        >
          {rebuilding ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileSearch className="h-3.5 w-3.5 mr-1" />}
          Recalculează stocuri
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[280px] max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută SKU, titlu sau furnizor..."
            className="pl-9 pr-8 h-9 bg-secondary/40 border-border"
            data-testid="stock-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={supplierFilter || "ALL"} onValueChange={(v) => setSupplierFilter(v === "ALL" ? "" : v)}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="stock-supplier-filter">
            <SelectValue placeholder="Toți furnizorii" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Toți furnizorii</SelectItem>
            <SelectItem value="none">Fără furnizor</SelectItem>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-9" onClick={() => setStockInOpen(true)} data-testid="stock-in-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Intrări marfă
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9"
          onClick={() => setReservationTagsOpen(true)}
          data-testid="open-reservation-tags-settings"
          title="Configurează ce tag-uri Shopify contează ca «rezervat»"
        >
          <Tag className="h-3.5 w-3.5 mr-1.5" /> Taguri rezervare
        </Button>
      </div>

      {/* Filter pills — status produs (Active/Draft/Arhivate) */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Status produs:</span>
        {PRODUCT_STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setProductStatusFilter(f.id)}
            data-testid={`stock-product-status-${f.id}`}
            className={`text-[11px] px-3 py-1 rounded-full transition-colors ${
              productStatusFilter === f.id
                ? "bg-foreground text-background"
                : "bg-secondary/30 text-muted-foreground hover:bg-secondary/60"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`stock-filter-${f.id}`}
            className={`text-[11px] px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
              filter === f.id
                ? "bg-primary text-primary-foreground"
                : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
            }`}
          >
            {f.icon ? <f.icon className="h-3 w-3" /> : null}
            {f.label}
          </button>
        ))}
        <div className="ml-auto text-[11px] text-muted-foreground self-center pr-2" data-testid="stock-total">
          {total} produse
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-x-auto bg-card">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-secondary/40 sticky top-0">
            <tr>
              {["Produs","SKU","Furnizor","Stoc fizic","Rezervat","Disponibil","Necesar","V 7z","V 30z","Prag","Status","Acțiuni"]
                .map((h) => (
                  <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="stock-table-body">
            {loading && items.length === 0 ? (
              [...Array(8)].map((_, i) => (
                <tr key={i} className="border-b border-border/50">
                  {[...Array(12)].map((__, j) => (
                    <td key={j} className="px-3 py-3"><div className="h-4 bg-secondary/40 animate-pulse rounded" /></td>
                  ))}
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center py-12 text-muted-foreground">
                  Niciun produs. Rulează Init în antetul Stocuri.
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <StockRow
                  key={it.id}
                  item={it}
                  suppliers={suppliers}
                  onAudit={() => setAuditItem(it)}
                  onChanged={(patch) => { onItemChanged(it.id, patch); onChanged?.(); }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Stock-in dialog */}
      <StockInDialog
        open={stockInOpen}
        onClose={() => setStockInOpen(false)}
        suppliers={suppliers}
        onDone={async () => { setStockInOpen(false); await reload(); onChanged?.(); }}
      />

      {/* Per-item audit drawer */}
      <AuditDrawer item={auditItem} onClose={() => setAuditItem(null)} />
      <ReservationTagsDialog
        open={reservationTagsOpen}
        onOpenChange={setReservationTagsOpen}
        onSaved={() => { reload(); onChanged?.(); }}
      />
    </div>
  );
}


function StockRow({ item, suppliers, onChanged, onAudit }) {
  const sb = STATUS_STYLES[item.status] || STATUS_STYLES.ok;

  const [minStock, setMinStock] = useState(item.min_stock);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setMinStock(item.min_stock); setDirty(false); }, [item.min_stock]);

  const saveMin = async () => {
    setSaving(true);
    try {
      const out = await updateItem(item.id, { min_stock: parseInt(minStock, 10) || 0 });
      onChanged?.({ min_stock: out.min_stock, status: undefined, reorder_qty: undefined });
      toast.success("Prag salvat");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSaving(false); }
  };

  const setSupplier = async (sid) => {
    try {
      const out = await updateItem(item.id, { supplier_id: sid === "NONE" ? null : sid });
      onChanged?.({ supplier_id: out.supplier_id, supplier_name: out.supplier_name });
      toast.success("Furnizor actualizat");
    } catch (e) {
      toast.error("Eroare furnizor");
    }
  };

  return (
    <tr className="border-b border-border/50 hover:bg-secondary/20 group" data-testid={`stock-row-${item.id}`}>
      <td className="px-3 py-2 max-w-[280px]">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded bg-secondary overflow-hidden shrink-0 flex items-center justify-center">
            {item.image ? (
              <img src={item.image} alt="" loading="lazy" className="h-full w-full object-cover" />
            ) : <ImageOff className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{item.title}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{item.sku || "—"}</td>
      <td className="px-3 py-2">
        <Select value={item.supplier_id || "NONE"} onValueChange={setSupplier}>
          <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`stock-supplier-${item.id}`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NONE">Fără furnizor</SelectItem>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums">
        <StockAdjuster item={item} onChanged={onChanged} />
      </td>
      <td className="px-3 py-2 text-right font-mono text-sm tabular-nums">
        {item.reserved > 0 ? (
          <span className="text-blue-500 font-semibold" title="Cantitate promisă în comenzi confirmate, nelivrate">{item.reserved}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono text-sm tabular-nums">
        {(() => {
          const a = item.available ?? 0;
          if (a < 0) return <span className="text-purple-500 font-bold" title="Disponibil real este negativ — trebuie aprovizionat">{a}</span>;
          if (a === 0) return <span className="text-red-500 font-semibold">0</span>;
          return <span className="text-foreground">{a}</span>;
        })()}
      </td>
      <td className="px-3 py-2 text-right font-mono text-sm tabular-nums">
        {(item.needed_qty ?? 0) > 0 ? (
          <span className="text-amber-500 font-semibold" title={item.estimated_value > 0 ? `≈ ${item.estimated_value} RON` : ""}>{item.needed_qty}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{item.sold_7d || 0}</td>
      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{item.sold_30d || 0}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={minStock}
            onChange={(e) => { setMinStock(e.target.value); setDirty(true); }}
            className="w-16 h-7 px-2 text-xs bg-transparent hover:bg-secondary/50 focus:bg-background focus:ring-1 focus:ring-primary outline-none rounded tabular-nums font-mono text-right"
            data-testid={`stock-min-${item.id}`}
          />
          {dirty && (
            <Button size="sm" className="h-6 px-2 bg-amber-500 hover:bg-amber-600 text-white" onClick={saveMin} disabled={saving} data-testid={`stock-save-min-${item.id}`}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <Badge variant="outline" className={`text-[10px] ${sb.className}`}>{sb.label}</Badge>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            onClick={onAudit}
            className="text-muted-foreground hover:text-primary p-0.5 rounded hover:bg-secondary/40"
            title="Vezi ledger audit (intrări vs vânzări)"
            data-testid={`stock-audit-${item.id}`}
          >
            <History className="h-3.5 w-3.5" />
          </button>
          <a
            href={`https://ieftinit-2841.myshopify.com/admin/products/${item.shopify_product_id}`}
            target="_blank" rel="noreferrer"
            className="text-muted-foreground hover:text-foreground"
            title="Shopify"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </td>
    </tr>
  );
}


function StockAdjuster({ item, onChanged }) {
  const [val, setVal] = useState(item.warehouse_qty);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(item.warehouse_qty); }, [item.warehouse_qty]);

  const save = async () => {
    const next = parseInt(val, 10);
    if (Number.isNaN(next) || next === item.warehouse_qty) { setEditing(false); return; }
    setSaving(true);
    try {
      const out = await adjustStock(item.id, { set_to: next, notes: "Ajustare manuală din tabel" });
      onChanged?.({ warehouse_qty: out.warehouse_qty });
      toast.success(`Stoc actualizat: ${item.warehouse_qty} → ${out.warehouse_qty}`);
      setEditing(false);
    } catch (e) {
      toast.error("Eroare");
    } finally { setSaving(false); }
  };

  const colored = item.warehouse_qty < 0 ? "text-purple-500" : item.warehouse_qty === 0 ? "text-red-500" : "text-foreground";

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={`font-mono font-semibold hover:bg-secondary/50 px-2 py-1 rounded transition ${colored}`}
        data-testid={`stock-qty-${item.id}`}
      >
        {item.warehouse_qty}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1 justify-end">
      <input
        type="number"
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        onBlur={save}
        className="w-20 h-7 px-2 text-sm bg-background border border-primary rounded outline-none font-mono text-right"
        data-testid={`stock-qty-input-${item.id}`}
      />
      {saving && <Loader2 className="h-3 w-3 animate-spin" />}
    </div>
  );
}


function StockInDialog({ open, onClose, suppliers, onDone }) {
  const [supplierId, setSupplierId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([]); // {item, quantity}
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setLines([]); setSearch(""); setResults([]); setNotes(""); setSupplierId(""); }
  }, [open]);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await listItems({ search, limit: 8 });
        setResults(r.items || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const addLine = (item) => {
    if (lines.find((l) => l.item.id === item.id)) return;
    setLines((prev) => [...prev, { item, quantity: 1 }]);
    setSearch("");
    setResults([]);
  };
  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.item.id !== id));
  const setQty = (id, q) => setLines((prev) => prev.map((l) => l.item.id === id ? { ...l, quantity: q } : l));

  const submit = async () => {
    const payload = {
      supplier_id: supplierId || null,
      notes: notes || null,
      lines: lines
        .map((l) => ({ inventory_item_id: l.item.id, quantity: parseInt(l.quantity, 10) }))
        .filter((l) => l.quantity > 0),
    };
    if (!payload.lines.length) return toast.error("Adaugă cel puțin un produs");
    setSubmitting(true);
    const tid = toast.loading("Salvez intrarea de marfă...");
    try {
      const r = await stockIn(payload);
      toast.success(`Intrare salvată: ${r.lines} linii · ${r.total_qty} buc`, { id: tid });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare", { id: tid });
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="stock-in-dialog">
        <DialogHeader className="p-6 pb-3 border-b border-border shrink-0">
          <DialogTitle>Intrări marfă</DialogTitle>
          <DialogDescription>Înregistrează marfă primită de la furnizor. Stocul real se actualizează imediat.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-6 pt-4 min-h-0 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Furnizor (opțional)</div>
              <Select value={supplierId || "NONE"} onValueChange={(v) => setSupplierId(v === "NONE" ? "" : v)}>
                <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Fără furnizor</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Note</div>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ex: Factură #123" data-testid="stock-in-notes" />
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Caută și adaugă produse</div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SKU sau titlu..."
              data-testid="stock-in-search"
            />
            {results.length > 0 && (
              <div className="mt-2 bg-popover border border-border rounded-md shadow-sm max-h-60 overflow-y-auto">
                {results.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => addLine(r)}
                    className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center gap-2 border-b border-border/50 last:border-b-0"
                    data-testid={`stock-in-result-${r.id}`}
                  >
                    <div className="h-7 w-7 rounded bg-secondary overflow-hidden shrink-0">
                      {r.image && <img src={r.image} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{r.title}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{r.sku || "—"} · stoc curent: {r.warehouse_qty}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {lines.length > 0 && (
            <div className="space-y-2" data-testid="stock-in-lines">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Linii intrare ({lines.length})
              </div>
              {lines.map((l) => (
                <div key={l.item.id} className="flex items-center gap-2 bg-secondary/30 rounded p-2">
                  <div className="h-7 w-7 rounded bg-secondary overflow-hidden shrink-0">
                    {l.item.image && <img src={l.item.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{l.item.title}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{l.item.sku || "—"}</div>
                  </div>
                  <Input
                    type="number"
                    value={l.quantity}
                    onChange={(e) => setQty(l.item.id, e.target.value)}
                    className="w-20 h-8 text-right tabular-nums"
                    data-testid={`stock-in-qty-${l.item.id}`}
                  />
                  <button
                    onClick={() => removeLine(l.item.id)}
                    className="text-muted-foreground hover:text-red-500 shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="p-4 border-t border-border bg-card/60 shrink-0">
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={submitting || lines.length === 0} data-testid="stock-in-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Boxes className="h-4 w-4 mr-2" />}
            Salvează intrarea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const TYPE_BADGE = {
  stock_in:       { label: "Intrare marfă",  class: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  manual_in:      { label: "Intrare marfă",  class: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  stock_in_bundle:{ label: "Intrare bundle", class: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  manual_adjust:  { label: "Ajustare",       class: "bg-amber-50 text-amber-700 border-amber-200" },
  adjustment:     { label: "Ajustare",       class: "bg-amber-50 text-amber-700 border-amber-200" },
  return_restock: { label: "Retur (+stoc)",  class: "bg-blue-50 text-blue-700 border-blue-200" },
  sale:           { label: "Vânzare fulfilled", class: "bg-rose-50 text-rose-700 border-rose-200" },
  sale_reverted:  { label: "Anulată",        class: "bg-violet-50 text-violet-700 border-violet-200" },
};

function AuditDrawer({ item, onClose }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item) { setAudit(null); return; }
    let alive = true;
    setLoading(true);
    getItemAudit(item.id, 500)
      .then((d) => alive && setAudit(d))
      .catch(() => alive && setAudit(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [item?.id]); // eslint-disable-line

  if (!item) return null;
  const t = audit?.totals || {};
  const wq = audit?.item?.warehouse_qty ?? item.warehouse_qty;

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="!max-w-3xl sm:!max-w-3xl flex flex-col" data-testid="audit-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Audit stoc · {item.title}</SheetTitle>
          <SheetDescription className="font-mono text-xs">SKU: {item.sku || "—"} · variant: {item.variant_id || "—"}</SheetDescription>
        </SheetHeader>

        {/* Totals strip */}
        <div className="grid grid-cols-4 gap-2 mt-4">
          <AuditCell label="Intrări marfă" value={`+${t.stock_in || 0}`} accent="emerald" />
          <AuditCell label="Ajustări" value={(t.adjustment >= 0 ? "+" : "") + (t.adjustment || 0)} accent="amber" />
          <AuditCell label="Vânzări fulfilled" value={t.sale || 0} accent="rose" />
          <AuditCell label="Stoc final" value={wq} accent={wq < 0 ? "violet" : wq === 0 ? "rose" : "primary"} bold />
        </div>

        <div className="text-[10px] text-muted-foreground mt-2 font-mono">
          {t.total_movements || 0} mișcări totale · formula: Σ(intrări) − Σ(vânzări fulfilled) = stoc final
        </div>

        {/* Ledger table */}
        <div className="mt-4 flex-1 overflow-auto border border-border rounded-lg">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : !audit?.rows?.length ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              <FileSearch className="h-8 w-8 mx-auto mb-2" />
              <p>Nicio mișcare în ledger pentru acest produs.</p>
              <p className="text-xs mt-1">Adaugă o intrare marfă din butonul „Intrări marfă" de mai sus.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Data</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Tip</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Delta</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Balans</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Referință</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {audit.rows.map((r, idx) => {
                  const tb = TYPE_BADGE[r.type] || { label: r.type, class: "bg-zinc-50 text-zinc-700 border-zinc-200" };
                  const dColor = (r.delta || 0) > 0 ? "text-emerald-600" : (r.delta || 0) < 0 ? "text-rose-600" : "text-muted-foreground";
                  return (
                    <tr key={r.id || idx} className="hover:bg-secondary/30" data-testid={`audit-row-${r.id || idx}`}>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${tb.class}`}>{tb.label}</Badge>
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold tabular-nums ${dColor}`}>
                        {(r.delta > 0 ? "+" : "") + (r.delta || 0)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{r.balance}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.order_number ? <span className="font-mono">#{r.order_number}</span> : r.notes ? <span className="text-muted-foreground">{r.notes}</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AuditCell({ label, value, accent, bold }) {
  const colors = {
    primary: "border-primary/40 bg-primary/5",
    emerald: "border-emerald-200 bg-emerald-50",
    rose:    "border-rose-200 bg-rose-50",
    amber:   "border-amber-200 bg-amber-50",
    violet:  "border-violet-200 bg-violet-50",
  };
  return (
    <div className={`rounded-lg border ${colors[accent] || colors.primary} p-3`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`mt-1 tabular-nums font-mono ${bold ? "font-display text-2xl font-bold" : "text-lg font-semibold"}`}>{value}</div>
    </div>
  );
}

